import { getGlobalConfigElement } from '../../utils/get-global-config-element';
import { PageContextStory, Story } from '../build/types';
import { NavigationService } from '../navigation';
import { StoryblokService } from '../storyblok';

export class LanguageService {
  static readThisArticleIn = {
    en: 'Read this article in English',
    de: 'Lesen Sie diesen Artikel auf Deutsch',
    fr: 'Lire cet article en Français',
    es: 'Lea este artículo en Español',
    cn: '点击查看中文版',
  };

  static getDefaultLocale(): string {
    return process.env.GATSBY_STORYBLOK_SPACE_DEFAULT_LANGUAGE;
  }

  static async getSpaceLanguages(): Promise<string[]> {
    const allLinks = await StoryblokService.getLinks('');
    return [...new Set(allLinks.map(({ slug }) => this.getLocaleFromSlug(slug)))] as string[];
  }

  static getLocaleFromSlug(slug: string): string {
    const sanitizedSlug = slug?.endsWith('/') ? slug : `${slug}/`;
    const languageCodeMatches = (
      `/${sanitizedSlug}`.replace('//', '/').match(/^\/([\w]{2})-([\w]{2})\/|^\/([\w]{2})\//) ?? []
    ).map((match) => match?.split('/').join(''));
    return languageCodeMatches[0] || this.getDefaultLocale();
  }

  static isPageDefaultLanguage(locale?: string): boolean {
    if (!locale && typeof document === 'undefined') {
      return true;
    }
    return this.getDefaultLocale() === (locale || document.documentElement.lang);
  }

  static getActiveLanguage(): string {
    if (typeof document === 'undefined') {
      return this.getDefaultLocale();
    }
    const actualPagePath = NavigationService.getCurrentPagePath();
    return this.getLocaleFromSlug(actualPagePath);
  }

  static getParsedLocale(locale: string): string {
    return locale === 'default' ? this.getDefaultLocale() : locale;
  }

  static getLocaleAwareLink(path: string, locale: string): string {
    if (!path) return '';

    const pathTokens = this.getPathTokens(this.removeLeadingSlash(path));
    const [firstPathElement, ...rest] = pathTokens;

    if (
      this.isLocale(firstPathElement) &&
      this.isSpaceSingleLanguage()
      // && !BuildService.isClientOnlyPath(path)
    ) {
      return `/${pathTokens.join('/')}`;
    }

    const pathWithoutLocale = this.isLocale(firstPathElement) ? rest : pathTokens;
    const localizedPath = this.isPageDefaultLanguage(locale) ? pathWithoutLocale : [locale, ...pathWithoutLocale];

    return `/${localizedPath.join('/')}`;
  }

  static isLocale(locale: string): boolean {
    const localeRegex = /^[A-Za-z]{2}$/;
    return localeRegex.test(locale);
  }

  static getAdditionalLanguages(): Array<string> {
    return Object.keys(this.readThisArticleIn);
  }

  static getHomePageUrl(homeStory: Story<PageContextStory>): string {
    const fallbackPath = '/';
    if (!homeStory) return fallbackPath;
    const { full_slug: slug } = homeStory;
    const lang = this.getLocaleFromSlug(slug);
    const path = lang === this.getDefaultLocale() ? fallbackPath : slug;
    return this.getLocaleAwareLink(path, this.getParsedLocale(lang));
  }

  private static getPathTokens(path: string, separator = '/'): string[] {
    return path ? path.split(separator) : [];
  }

  private static isSpaceSingleLanguage(): boolean {
    if (typeof document === 'undefined') {
      return true;
    }
    const globalConfigElement = getGlobalConfigElement();
    const languages = globalConfigElement?.dataset.languages || '[]';
    return JSON.parse(languages).length <= 1;
  }

  private static removeLeadingSlash(path: string): string {
    return path.startsWith('/') ? path.substring(1) : path;
  }

  // static getAllLinks(allEntries: Story[]) {
  //   return allEntries.reduce((acc, entry) => ({ ...acc, [entry.id]: entry }), {});
  // }

  // {
  // ...acc, [entry.id]: entry }
  //   acc[entry.id] = {
  //     parent_id: entry.parent_id,
  //     slug: entry.full_slug,
  //   };
  //   return acc;
  // }, {} as Record<number, any>);
  // }, {} as Record<number, Story>);
  // }

  // const createMapFromId = (list: StoryblokLinkNode[]): LinkMap => list
  //     .reduce((a, v) => ({ ...a, [v.id]: v }), {});

  // TODO Find a better logic, possibly use GraphQL queries
  //   static parseTranslationDatasource(
  //     sources: Record<string, string>[]
  //   ): Record<string, string> {
  //     return sources.reduce((accumulator, source) => {
  //       if (!source) return accumulator;
  //       const { dimension_value: translatedValue, name, value } = source;
  //       return { ...accumulator, [name]: translatedValue || value };
  //     }, {});
  //   }

  //   private static parseTranslationDatasourceWithDefaultNameAsKey(
  //     sources: Record<string, string>[]
  //   ): Record<string, string> {
  //     return sources.reduce((accumulator, source) => {
  //       if (!source) return accumulator;
  //       const { dimension_value: translatedValue, name, value } = source;
  //       if (name === value) {
  //         return { ...accumulator, [name]: translatedValue || value };
  //       }
  //       return {
  //         ...accumulator,
  //         [name]: translatedValue || value,
  //         [value]: translatedValue || value,
  //       };
  //     }, {});
  //   }

  //   private async getDatasourceWithDimensionValuesFromLocales(
  //     locales: string[],
  //     datasourceName: string
  //   ): Promise<StoryblokResult[][]> {
  //     const datasourceDimensions =
  //       await this.storyblokService.getDatasourceDimensionNames(datasourceName);

  //     const localeDimensionNamesToFetch = locales.map((locale) => {
  //       const localeDimension = datasourceDimensions.find(
  //         (dimension) =>
  //           dimension.entry_value?.toLocaleLowerCase() ===
  //           locale.toLocaleLowerCase()
  //       );
  //       return localeDimension ? localeDimension.entry_value : locale;
  //     });

  //     return Promise.all(
  //       localeDimensionNamesToFetch.map((locale: string) =>
  //         this.storyblokService.getDatasourcesEntries(datasourceName, {
  //           dimension: locale,
  //           cv: Date.now(),
  //         })
  //       )
  //     );
  //   }

  //   async fetchTranslations(
  //     datasourceName: string
  //   ): Promise<Record<string, Record<string, string>>> {
  //     const locales = await this.getSpaceLanguages();
  //     const datasources = await this.getDatasourceWithDimensionValuesFromLocales(
  //       locales,
  //       datasourceName
  //     );

  //     return locales.reduce(
  //       (accumulator, value, i) => {
  //         if (value) {
  //           accumulator[value] = LanguageService.parseTranslationDatasource(
  //             datasources[i][0].data.datasource_entries
  //           );
  //         }
  //         return accumulator;
  //       },
  //       {} as Record<string, Record<string, string>>
  //     );
  //   }

  //   async fetchTranslationsWithDefaultValueAsKey(
  //     datasourceName: string
  //   ): Promise<Record<string, Record<string, string>>> {
  //     const locales = await this.getSpaceLanguages();
  //     const datasources = await this.getDatasourceWithDimensionValuesFromLocales(
  //       locales,
  //       datasourceName
  //     );

  //     return locales.reduce(
  //       (accumulator, value, i) => {
  //         if (value) {
  //           accumulator[value] =
  //             LanguageService.parseTranslationDatasourceWithDefaultNameAsKey(
  //               datasources[i][0].data.datasource_entries
  //             );
  //         }
  //         return accumulator;
  //       },
  //       {} as Record<string, Record<string, string>>
  //     );
  //   }

  //   async getAllLocales(): Promise<string[]> {
  //     try {
  //       const datasourceResponses = await this.getDatasources([
  //         'space-languages',
  //       ]);

  //       if (datasourceResponses && datasourceResponses.length > 0) {
  //         const locales = datasourceResponses
  //           .flatMap(({ data }) => data.datasource_entries)
  //           .map((entry) => entry.name);

  //         return locales.length > 0 ? locales : await this.getSpaceLanguages();
  //       }
  //       return await this.getSpaceLanguages();
  //     } catch (error) {
  //       console.error('Error fetching locales:', error);
  //       return await this.getSpaceLanguages();
  //     }
  //   }
}
